import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import RichTextEditor from "../../../components/common/RichTextEditor";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE = "https://tyka.premierhostings.com/beta/api";
const bannerTypes = ["main"];
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

const newBanner = () => ({
  clientId: crypto.randomUUID(),
  type: "main",
  image: null,
  preview: "",
});

const AddChildCategory = () => {
  const navigate = useNavigate();
  const mediaInputRef = useRef(null);

  const [headCategories, setHeadCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [filteredParentCategories, setFilteredParentCategories] = useState([]);

  // Category media (single image OR video)
  const [categoryMedia, setCategoryMedia] = useState(null);
  const [categoryMediaPreview, setCategoryMediaPreview] = useState("");
  const [categoryType, setCategoryType] = useState(""); // "image" | "video" | ""

  const [selectedHeadId, setSelectedHeadId] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [banners, setBanners] = useState([newBanner()]);

  // Keep latest blob URLs in refs so unmount cleanup can revoke them
  const mediaPreviewRef = useRef("");
  const bannersRef = useRef(banners);
  useEffect(() => {
    mediaPreviewRef.current = categoryMediaPreview;
  }, [categoryMediaPreview]);
  useEffect(() => {
    bannersRef.current = banners;
  }, [banners]);

  // Fetch every page of a paginated endpoint
  const fetchAllPages = async (url) => {
    let allData = [];
    let currentPage = 1;
    let lastPage = 1;

    try {
      do {
        const res = await axios.get(`${url}?page=${currentPage}`);
        allData = [...allData, ...(res.data.data || [])];
        lastPage = res.data.meta?.last_page || 1;
        currentPage++;
      } while (currentPage <= lastPage);
    } catch (err) {
      console.error("Error fetching all pages:", err);
    }

    return allData;
  };

  // Fetch head & parent categories from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headsRes = await axios.get(`${API_BASE}/product-categories`);
        const parents = await fetchAllPages(
          `${API_BASE}/product-sub-categories`,
        );

        setHeadCategories(headsRes.data?.data || []);
        setParentCategories(parents);
      } catch (error) {
        toast.error("Failed to load categories. Please try again.");
        console.error("API fetch error:", error);
      }
    };

    fetchData();
  }, []);

  // Filter parents when head is selected
  useEffect(() => {
    if (selectedHeadId && parentCategories.length > 0) {
      const filtered = parentCategories.filter(
        (p) => p.product_category_id?.toString() === selectedHeadId,
      );
      setFilteredParentCategories(filtered);

      if (!filtered.some((p) => p.id.toString() === parentCategoryId)) {
        setParentCategoryId("");
      }
    } else {
      setFilteredParentCategories([]);
      setParentCategoryId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHeadId, parentCategories]);

  // Auto-generate slug from name
  useEffect(() => {
    const generatedSlug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/[\s-]+/g, "-")
      .replace(/^-|-$/g, "");
    setSlug(generatedSlug);
  }, [name]);

  // Revoke blob preview URLs on unmount
  useEffect(() => {
    return () => {
      if (mediaPreviewRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(mediaPreviewRef.current);
      }
      bannersRef.current.forEach((b) => {
        if (b.preview?.startsWith("blob:")) URL.revokeObjectURL(b.preview);
      });
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!selectedHeadId) newErrors.head = "Please select a head category.";
    if (!parentCategoryId)
      newErrors.parent = "Please select a parent category.";
    if (!name.trim()) newErrors.name = "Name is required.";
    else if (!nameRegex.test(name))
      newErrors.name = "Name should only contain letters and spaces.";
    if (!slug.trim()) newErrors.slug = "Slug is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      Object.values(newErrors).forEach((err) => toast.error(err));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      Swal.fire({
        title: "Saving...",
        text: "Please wait while we add the child category",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = new FormData();

      formData.append("product_category_id", selectedHeadId);
      formData.append("product_subcategory_id", parentCategoryId);
      formData.append("name", name);
      formData.append("slug", slug);
      formData.append("description", description);
      formData.append("category_id", selectedHeadId);

      // Category image/video as a normal file
      if (categoryMedia) {
        if (categoryType === "video") {
          formData.append("video", categoryMedia);
        } else {
          formData.append("category_image", categoryMedia);
        }
      }

      // Only banners that actually have an image; keep indexes continuous
      banners
        .filter((banner) => banner.image instanceof File)
        .forEach((banner, index) => {
          formData.append(`banners[${index}][type]`, banner.type);
          formData.append(
            `banners[${index}][image]`,
            banner.image,
            banner.image.name,
          );
        });

      await axios.post(`${API_BASE}/product-child-categories`, formData);

      Swal.close();
      toast.success("Child category added successfully!");
      setTimeout(() => navigate("/childcategory"), 1500);
    } catch (error) {
      Swal.close();

      toast.error(
        error.response?.data?.message || "Failed to add child category.",
      );
      console.error("Submit error:", error.response?.data || error);
    }
  };

  const clearCategoryMedia = () => {
    if (categoryMediaPreview.startsWith("blob:")) {
      URL.revokeObjectURL(categoryMediaPreview);
    }
    setCategoryMedia(null);
    setCategoryMediaPreview("");
    setCategoryType("");
    if (mediaInputRef.current) mediaInputRef.current.value = "";
  };

  const handleCategoryMedia = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast.error("Please select a valid image or video file.");
      e.target.value = "";
      return;
    }

    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast.error(`Video size must be ${MAX_VIDEO_SIZE_MB}MB or less.`);
      e.target.value = "";
      return;
    }

    // Revoke previous local preview URL to avoid leaking memory
    if (categoryMediaPreview.startsWith("blob:")) {
      URL.revokeObjectURL(categoryMediaPreview);
    }

    setCategoryMedia(file);
    setCategoryMediaPreview(URL.createObjectURL(file));
    setCategoryType(isVideo ? "video" : "image");
  };

  const handleBannerTypeChange = (index, value) => {
    setBanners((prev) =>
      prev.map((b, i) => (i === index ? { ...b, type: value } : b)),
    );
  };

  const handleBannerImageChange = (index, file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image.");
      return;
    }

    setBanners((prev) =>
      prev.map((banner, i) => {
        if (i !== index) return banner;

        if (banner.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(banner.preview);
        }

        return {
          ...banner,
          image: file,
          preview: URL.createObjectURL(file),
        };
      }),
    );
  };

  const addBanner = () => {
    setBanners((prev) => [...prev, newBanner()]);
  };

  const removeBanner = (index) => {
    setBanners((prev) => {
      const target = prev[index];
      if (target?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 dark:text-white">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold">Add Child Category</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 dark:bg-gray-800 rounded-lg p-6"
      >
        {/* Head Category */}
        <div>
          <label
            htmlFor="headCategory"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Head Category <span className="text-red-500">*</span>
          </label>
          <select
            id="headCategory"
            value={selectedHeadId}
            onChange={(e) => setSelectedHeadId(e.target.value)}
            className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 ${
              errors.head ? "border-red-500" : ""
            }`}
          >
            <option value="">Select a Head Category</option>
            {headCategories.map((head) => (
              <option key={head.id} value={head.id.toString()}>
                {head.name}
              </option>
            ))}
          </select>
        </div>

        {/* Parent Category */}
        <div>
          <label
            htmlFor="parentCategory"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Parent Category <span className="text-red-500">*</span>
          </label>
          <select
            id="parentCategory"
            value={parentCategoryId}
            onChange={(e) => setParentCategoryId(e.target.value)}
            className={`w-full border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 ${
              errors.parent ? "border-red-500" : ""
            }`}
            disabled={!selectedHeadId || filteredParentCategories.length === 0}
          >
            <option value="">Select a Parent Category</option>
            {filteredParentCategories.map((parent) => (
              <option key={parent.id} value={parent.id.toString()}>
                {parent.name}
              </option>
            ))}
          </select>
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="childCategoryName"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            id="childCategoryName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${errors.name ? "border-red-500" : ""} w-full`}
          />
        </div>

        {/* Slug */}
        <div>
          <label
            htmlFor="slug"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Slug <span className="text-red-500">*</span>
          </label>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className={`${errors.slug ? "border-red-500" : ""} w-full`}
          />
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="description"
            className="block font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Description
          </label>
          <RichTextEditor value={description} onChange={setDescription} />
        </div>

        {/* Category Image / Video */}
        <div className="w-full">
          <label
            htmlFor="category-media-input"
            className="block mb-2 font-medium"
          >
            Category Video
          </label>

          <input
            id="category-media-input"
            ref={mediaInputRef}
            type="file"
            accept="image/*,.mp4,.mov,.avi,.mkv,.webm"
            onChange={handleCategoryMedia}
            className="block w-full"
          />

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            You can upload either one image or one video. Maximum video size is{" "}
            {MAX_VIDEO_SIZE_MB}MB.
          </p>

          {categoryType === "image" && categoryMediaPreview && (
            <div className="mt-3 flex w-full flex-col gap-3">
              <button
                type="button"
                onClick={clearCategoryMedia}
                className="self-end rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete Image
              </button>

              <img
                src={categoryMediaPreview}
                className="h-60 w-full rounded object-cover"
                alt="Category preview"
              />
            </div>
          )}

          {categoryType === "video" && categoryMediaPreview && (
            <div className="mt-3 flex w-full flex-col gap-3">
              <button
                type="button"
                onClick={clearCategoryMedia}
                className="self-end shrink-0 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete Video
              </button>

              <video
                src={categoryMediaPreview}
                controls
                className="h-60 w-full rounded object-cover"
              />
            </div>
          )}
        </div>

        {/* Banners */}
        <div className="space-y-5">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Banners</h3>

            <Button
              type="button"
              onClick={addBanner}
              className="bg-green-600 hover:bg-green-700"
            >
              + Add More Banner
            </Button>
          </div>

          {banners.map((banner, index) => (
            <div
              key={banner.clientId}
              className="border rounded-lg p-4 space-y-4"
            >
              <div>
                <label className="block mb-2 font-medium">Banner Type</label>

                <select
                  value={banner.type}
                  onChange={(e) =>
                    handleBannerTypeChange(index, e.target.value)
                  }
                  className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600"
                >
                  {bannerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-medium">Banner Image</label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleBannerImageChange(index, e.target.files?.[0])
                  }
                  className="block w-full border rounded-lg p-2"
                />

                {banner.preview && (
                  <img
                    src={banner.preview}
                    alt="Banner preview"
                    className="mt-3 h-40 rounded border object-cover"
                  />
                )}
              </div>

              {banners.length > 1 && (
                <Button
                  type="button"
                  onClick={() => removeBanner(index)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/childcategory")}
            className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            Cancel
          </Button>
          <Button type="submit" className="bg-red-600 hover:bg-red-700">
            Save
          </Button>
        </div>
      </form>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AddChildCategory;
